"use client"

import React, { useState, useEffect } from "react"
import { Modal, Button, Form, Spinner, ListGroup } from "react-bootstrap"
import Swal from "sweetalert2"
import { UserModel } from "../../../models/UserModel"
import { message } from "antd"
import axios from "axios"
import { 
  FaTag, 
  FaPercentage, 
  FaMagic, 
  FaPaperPlane, 
  FaListAlt,
  FaCheck,
  FaInfoCircle,
  FaRandom
} from "react-icons/fa"

interface PromoCode {
  id: number
  code: string
  description: string
  discountPercentage: number
}

interface PromoCodeModalProps {
  show: boolean
  onClose: () => void
  user?: UserModel
  onCreated?: () => void
}

const PromoCodeModal: React.FC<PromoCodeModalProps> = ({ show, onClose, user, onCreated }) => {
  const [newCode, setNewCode] = useState("")
  const [description, setDescription] = useState("")
  const [discountPercentage, setDiscountPercentage] = useState(100)
  const [submitting, setSubmitting] = useState(false)
  const [submittingMail, setSubmittingMail] = useState(false)
  const [showExistingModal, setShowExistingModal] = useState(false)
  const [existingCodes, setExistingCodes] = useState<PromoCode[]>([])
  const [loadingCodes, setLoadingCodes] = useState(false)
  const [selectedCode, setSelectedCode] = useState<PromoCode | null>(null)

  const COLORS = {
    primary: "#2A919D",
    secondary: "#4BB0C0",
    accent: "#FF6B6B",
    success: "#28A745",
    light: "#F8F9FA",
    dark: "#343A40",
    muted: "#6C757D",
    gradient: "linear-gradient(135deg, #2A919D 0%, #4BB0C0 100%)",
    shadow: "0 10px 30px rgba(42, 145, 157, 0.15)",
  }

  const styles: Record<string, React.CSSProperties> = {
    modalContent: { 
      background: "white",
      borderRadius: "0 0 12px 12px",
      maxHeight: "80vh",
      overflowY: "auto",
    },
    header: { 
      border: "none",
      background: COLORS.gradient,
      color: "white",
      borderTopLeftRadius: "12px",
      borderTopRightRadius: "12px",
      padding: "1.5rem",
    },
    title: { 
      color: "white",
      fontWeight: "600",
      fontSize: "1.25rem",
      display: "flex",
      alignItems: "center",
      gap: "0.5rem",
    },
    badge: {
      backgroundColor: COLORS.primary,
      color: "#fff",
      borderRadius: "20px",
      padding: "0.4rem 0.8rem",
      fontSize: "0.85rem",
      fontWeight: "600",
      display: "inline-flex",
      alignItems: "center",
      gap: "0.3rem",
    },
    listItemActive: { 
      backgroundColor: "rgba(42, 145, 157, 0.1)",
      borderLeft: `4px solid ${COLORS.primary}`,
      borderColor: "rgba(42, 145, 157, 0.2)",
    },
  }

  const generateRandomCode = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    let result = ""
    for (let i = 0; i < 8; i++) result += chars.charAt(Math.floor(Math.random() * chars.length))
    setNewCode(result)
  }

  const getDiscountBadgeStyle = (percentage: number) => {
    if (percentage === 100) return { ...styles.badge, backgroundColor: COLORS.success }
    if (percentage >= 50) return { ...styles.badge, backgroundColor: COLORS.primary }
    return { ...styles.badge, backgroundColor: COLORS.accent }
  }

  const showAlert = (type: "success" | "error", title: string, text?: string) => {
    Swal.fire({
      icon: type,
      title,
      text,
      confirmButtonColor: COLORS.primary,
      background: COLORS.light,
      customClass: {
        popup: 'animated bounceIn'
      }
    })
  }

  const handleCreatePromoCode = async () => {
    if (!newCode.trim()) return showAlert("error", "Erreur", "Veuillez entrer un code promo")
    if (newCode !== newCode.toUpperCase()) return showAlert("error", "Erreur", "Le code promo doit être en majuscules")

    setSubmitting(true)
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Token manquant")

      await axios.post(
        `${process.env.REACT_APP_API_URL}/promo-code`,
        { code: newCode.trim(), description: description.trim(), discountPercentage },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      showAlert("success", "Succès", "Code promo créé avec succès")
      setNewCode("")
      setDescription("")
      setDiscountPercentage(100)
      onClose()
      onCreated && onCreated()
    } catch (err: any) {
      console.error(err)
      showAlert("error", "Erreur", err.response?.data?.message || "Erreur lors de la création du code promo")
    } finally {
      setSubmitting(false)
    }
  }

  const sendPromoEmail = async (code: string, discount: number, desc: string) => {
    if (!user) return
    setSubmittingMail(true)
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Token manquant")

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/email/sendcodepromo`,
        { user, codePromo: code, discountPercentage: discount, description: desc },
        { headers: { Authorization: `Bearer ${token}` } }
      )

      if (res.data.success) {
        // Message de succès avec animation
        message.success({ 
          content: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaCheck style={{ color: '#52c41a' }} />
              <div>
                <strong>🎉 Email envoyé avec succès !</strong>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  Code: <strong>{code}</strong> | {discount}% de réduction
                </div>
              </div>
            </div>
          ), 
          duration: 5,
          style: {
            marginTop: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            borderRadius: '8px'
          }
        })

        // Alert optionnelle
        showAlert(
          "success", 
          "Email envoyé !", 
          `Le code ${code} (${discount}%) a été envoyé à ${user.email}`
        )
      } else {
        message.error({ 
          content: (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <FaInfoCircle style={{ color: '#ff4d4f' }} />
              <div>
                <strong>❌ Erreur lors de l'envoi</strong>
                <div style={{ fontSize: '12px', marginTop: '4px' }}>
                  Veuillez réessayer plus tard
                </div>
              </div>
            </div>
          ), 
          duration: 4 
        })
      }
    } catch (err) {
      console.error(err)
      message.error({ 
        content: (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaInfoCircle style={{ color: '#ff4d4f' }} />
            <div>
              <strong>❌ Erreur lors de l'envoi</strong>
              <div style={{ fontSize: '12px', marginTop: '4px' }}>
                {err instanceof Error ? err.message : 'Erreur inconnue'}
              </div>
            </div>
          </div>
        ), 
        duration: 4 
      })
    } finally {
      setSubmittingMail(false)
    }
  }

  const fetchExistingCodes = async () => {
    setLoadingCodes(true)
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Token manquant")

      const response = await axios.get(`${process.env.REACT_APP_API_URL}/promo-code`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      const codes = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data.promoCodes)
        ? response.data.promoCodes
        : []
      setExistingCodes(codes)
    } catch (err) {
      console.error("Erreur fetch codes:", err)
      message.error("Erreur lors du chargement des codes")
    } finally {
      setLoadingCodes(false)
    }
  }

  return (
    <>
      {/* Styles améliorés */}
      <style>{`
        .promo-code-modal .modal-content {
          border: none;
          border-radius: 12px;
          box-shadow: ${COLORS.shadow};
          overflow: hidden;
          z-index: 14000;
        }

        .promo-code-existing-modal .modal-content {
          border: none;
          border-radius: 12px;
          box-shadow: ${COLORS.shadow};
          overflow: hidden;
        }

        .btn-primary-gradient {
          background: ${COLORS.gradient};
          border: none;
          color: white;
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          min-width: 140px;
        }

        .btn-primary-gradient:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 20px rgba(42, 145, 157, 0.3);
          color: white;
        }

        .btn-primary-gradient:disabled {
          opacity: 0.7;
          transform: none !important;
          box-shadow: none !important;
        }

        .btn-outline-primary-custom {
          background: transparent;
          border: 2px solid ${COLORS.primary};
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-outline-primary-custom:hover {
          background: ${COLORS.primary};
          color: white;
          transform: translateY(-2px);
        }

        .btn-secondary-custom {
          background: white;
          border: 2px solid ${COLORS.primary};
          font-weight: 600;
          padding: 0.75rem 1.5rem;
          border-radius: 8px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .btn-secondary-custom:hover {
          background: ${COLORS.light};
          transform: translateY(-2px);
        }

        .form-control-custom {
          border: 2px solid #E9ECEF;
          border-radius: 8px;
          padding: 0.75rem;
          transition: all 0.3s ease;
        }

        .form-control-custom:focus {
          border-color: ${COLORS.primary};
          box-shadow: 0 0 0 0.2rem rgba(42, 145, 157, 0.15);
        }

        .slider-container {
          padding: 1rem;
          background: ${COLORS.light};
          border-radius: 8px;
          margin: 1rem 0;
        }

        .slider-value {
          font-size: 1.25rem;
          font-weight: 700;
          color: ${COLORS.primary};
          margin-right: 0.5rem;
        }

        .code-display {
          font-family: 'Courier New', monospace;
          font-size: 1.5rem;
          font-weight: 700;
          letter-spacing: 2px;
          color: ${COLORS.dark};
          background: ${COLORS.light};
          padding: 0.75rem;
          border-radius: 8px;
          text-align: center;
          margin: 1rem 0;
        }

        .info-tip {
          background: rgba(42, 145, 157, 0.1);
          border-left: 4px solid ${COLORS.primary};
          padding: 0.75rem;
          border-radius: 0 8px 8px 0;
          margin: 1rem 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .discount-indicator {
          height: 8px;
          background: ${COLORS.light};
          border-radius: 4px;
          margin: 1rem 0;
          overflow: hidden;
        }

        .discount-fill {
          height: 100%;
          background: ${COLORS.gradient};
          transition: width 0.3s ease;
          border-radius: 4px;
        }

        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }

        .pulse {
          animation: pulse 2s infinite;
        }

        .fade-in {
          animation: fadeIn 0.3s ease-in;
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }

                
        .promo-code-modal {
          z-index: 9999 !important;
        }
        
        .promo-code-modal .modal-content {
          z-index: 9999 !important;
        }
        
        .promo-code-existing-modal {
          z-index: 10000 !important;
        }
        
        .promo-code-existing-modal .modal-content {
          z-index: 10000 !important;
        }
        
        /* S'assurer que les overlays ont aussi le bon z-index */
        .modal-backdrop {
          z-index: 9998 !important;
        }
        
        .promo-code-modal.show ~ .modal-backdrop.show {
          z-index: 9998 !important;
        }
        
        .promo-code-existing-modal.show ~ .modal-backdrop.show {
          z-index: 9999 !important;
        }

        /* Styles pour les notifications personnalisées */
        .custom-notification {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 99999;
          animation: slideIn 0.3s ease;
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>

      {/* Modal principal */}
      <Modal 
        show={show && !showExistingModal} 
        onHide={onClose}
        backdrop={true}
        centered
        className="promo-code-modal fade-in"
        size="lg"
      >
        <Modal.Header closeButton style={styles.header} closeVariant="white">
          <Modal.Title style={styles.title}>
            <FaTag /> Créer un code promo
          </Modal.Title>
        </Modal.Header>

        <Modal.Body style={styles.modalContent}>
          <div className="p-4">
            <Form>
              {/* Code promo avec générateur */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold mb-2 d-flex align-items-center gap-2">
                  Code Promo <span className="text-danger">*</span>
                </Form.Label>
                
                <div className="code-display pulse">
                  {newCode || "______"}
                </div>
                
                <div className="d-flex gap-2 mb-3">
                  <Form.Control
                    type="text"
                    value={newCode}
                    onChange={(e) => setNewCode(e.target.value.toUpperCase())}
                    placeholder="Entrez un code (ex: PROMO50)"
                    className="form-control-custom flex-grow-1"
                  />
                  <Button 
                    className="btn-outline-primary-custom" 
                    onClick={generateRandomCode}
                    style={{ minWidth: "140px" }}
                  >
                    <FaRandom /> Générer
                  </Button>
                </div>
                
                <div className="info-tip">
                  <FaInfoCircle color={COLORS.primary} />
                  <small className="text-muted">
                    Le code doit être en majuscules, sans espaces. 8 caractères recommandés.
                  </small>
                </div>
              </Form.Group>

              {/* Barre de remise */}
              <Form.Group className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Label className="fw-bold d-flex align-items-center gap-2 mb-0">
                    <FaPercentage /> Pourcentage de remise
                  </Form.Label>
                  <div className="d-flex align-items-center">
                    <span className="slider-value">{discountPercentage}</span>
                    <span className="text-muted">%</span>
                    <div style={getDiscountBadgeStyle(discountPercentage)} className="ms-3">
                      <FaPercentage size={12} /> {discountPercentage}%
                    </div>
                  </div>
                </div>

                <div className="slider-container">
                  <Form.Range
                    min={0}
                    max={100}
                    step={1}
                    value={discountPercentage}
                    onChange={(e) => setDiscountPercentage(Number(e.target.value))}
                    className="w-100"
                    style={{ accentColor: COLORS.primary }}
                  />
                  
                  <div className="discount-indicator mt-3">
                    <div 
                      className="discount-fill"
                      style={{ width: `${discountPercentage}%` }}
                    />
                  </div>
                  
                  <div className="d-flex justify-content-between mt-2">
                    <small className="text-muted">0%</small>
                    <small className="text-muted">25%</small>
                    <small className="text-muted">50%</small>
                    <small className="text-muted">75%</small>
                    <small className="text-muted">100%</small>
                  </div>
                </div>
              </Form.Group>

              {/* Description */}
              <Form.Group className="mb-4">
                <Form.Label className="fw-bold d-flex align-items-center gap-2 mb-2">
                  Description <span className="text-muted fw-normal">(optionnelle)</span>
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="form-control-custom"
                  placeholder="Décrivez l'offre, les conditions, la durée..."
                />
                <small className="text-muted mt-2 d-block">
                  Cette description apparaîtra dans l'email envoyé au client.
                </small>
              </Form.Group>
            </Form>
          </div>
        </Modal.Body>

        <Modal.Footer style={{ 
          borderTop: `1px solid #E9ECEF`, 
          background: COLORS.light,
          padding: "1.5rem" 
        }}>
          <div className="d-flex flex-column flex-md-row w-100 gap-3">
            <div className="d-flex flex-column flex-md-row gap-3 w-100">
              <Button 
                className="btn-outline-primary-custom flex-fill"
                onClick={onClose}
                disabled={submitting}
              >
                Annuler
              </Button>

              {user && (
                <Button 
                  className="btn-secondary-custom flex-fill"
                  onClick={() => { 
                    fetchExistingCodes()
                    setShowExistingModal(true) 
                  }}
                >
                  <FaListAlt /> Codes existants
                </Button>
              )}
            </div>

            {user ? (
              <Button
                className="btn-primary-gradient"
                onClick={async () => {
                  await handleCreatePromoCode()
                  await sendPromoEmail(newCode, discountPercentage, description)
                }}
                disabled={submittingMail || !newCode.trim() || submitting}
                style={{ minWidth: "200px" }}
              >
                {submittingMail ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaPaperPlane /> Envoyer par email
                  </>
                )}
              </Button>
            ) : (
              <Button
                className="btn-primary-gradient"
                onClick={handleCreatePromoCode}
                disabled={submitting || !newCode.trim()}
                style={{ minWidth: "200px" }}
              >
                {submitting ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <>
                    <FaCheck /> Créer le code
                  </>
                )}
              </Button>
            )}
          </div>
        </Modal.Footer>
      </Modal>

      {/* Modal des codes existants */}
      <Modal 
        show={showExistingModal} 
        onHide={() => setShowExistingModal(false)} 
        centered
        backdrop={true}
        className="promo-code-existing-modal fade-in"
        size="lg"
      >
        <Modal.Header closeButton style={styles.header} closeVariant="white">
          <Modal.Title style={styles.title}>
            <FaListAlt /> Sélectionner un code existant
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body style={styles.modalContent}>
          <div className="p-4">
            {loadingCodes ? (
              <div className="text-center py-5">
                <Spinner animation="border" style={{ color: COLORS.primary }} />
                <p className="mt-3 text-muted">Chargement des codes promos...</p>
              </div>
            ) : existingCodes.length > 0 ? (
              <ListGroup style={{ maxHeight: "300px", overflowY: "auto" }} variant="flush">
                {existingCodes.map((code) => {
                  const isActive = selectedCode?.id === code.id
                  return (
                    <ListGroup.Item
                      key={code.id + "-" + code.code}
                      action
                      active={isActive}
                      onClick={() => setSelectedCode(isActive ? null : code)}
                      style={isActive ? styles.listItemActive : undefined}
                      className="py-3 px-4"
                    >
                      <div className="d-flex justify-content-between align-items-start">
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-3 mb-2">
                            <span className="badge" style={getDiscountBadgeStyle(code.discountPercentage)}>
                              {code.discountPercentage}%
                            </span>
                            <strong className="fs-5">{code.code}</strong>
                            {isActive && (
                              <FaCheck color={COLORS.primary} className="ms-2" />
                            )}
                          </div>
                          {code.description && (
                            <div className="text-muted mt-1">{code.description}</div>
                          )}
                        </div>
                      </div>
                    </ListGroup.Item>
                  )
                })}
              </ListGroup>
            ) : (
              <div className="text-center py-5">
                <FaTag size={48} color="#E9ECEF" />
                <p className="mt-3 text-muted">Aucun code promo disponible</p>
                <Button 
                  className="btn-outline-primary-custom mt-3"
                  onClick={() => setShowExistingModal(false)}
                >
                  Retour
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
        
        <Modal.Footer style={{ 
          borderTop: `1px solid #E9ECEF`, 
          background: COLORS.light,
          padding: "1.5rem" 
        }}>
          <div className="d-flex justify-content-between w-100 gap-3">
            <Button 
              className="btn-outline-primary-custom"
              onClick={() => setShowExistingModal(false)}
              style={{ minWidth: "120px" }}
            >
              Retour
            </Button>
            
            <Button
              className="btn-primary-gradient"
              disabled={!selectedCode || submittingMail}
              onClick={async () => {
                if (selectedCode) {
                  await sendPromoEmail(
                    selectedCode.code, 
                    selectedCode.discountPercentage, 
                    selectedCode.description || ""
                  )
                  setShowExistingModal(false)
                }
              }}
              style={{ minWidth: "160px" }}
            >
              {submittingMail ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <>
                  <FaPaperPlane /> Envoyer
                </>
              )}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default PromoCodeModal